import type { FeedLog } from '../types/production'
import type { InventoryItem } from '../types/inventory'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import { dexieFeedLogRepo, type FeedLogListParams } from '../offline/repos/DexieFeedLogRepo'
import { dexieLotCostEventRepo } from '../offline/repos/DexieLotCostEventRepo'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { buildFeedLogFingerprint, parseFeedLogFingerprintSource } from './feedLogIdentity'
import { createId } from './id'
import { api } from './api'
import { inventoryApi } from './inventoryApi'
import { lotCostEventApi } from './lotCostEventApi'
import { deriveFeedLogCost } from './feedLogCostDerivation'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const toOptionalDecimalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined
    return value.toString()
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || undefined
  }
  return undefined
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: FeedLogListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lotId: params.lotId?.trim() || '',
    lot: params.lot?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: FeedLogListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingFeedLogIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'feedLog')
      .map((entry) => entry.entityId)
  )
}

const getPendingFeedLogSyncMarkers = async (): Promise<{
  ids: Set<string>
  deleteFingerprints: Set<string>
}> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  const ids = new Set<string>()
  const deleteFingerprints = new Set<string>()

  for (const entry of entries) {
    if (entry.entityType !== 'feedLog') continue
    ids.add(entry.entityId)
    if (entry.op !== 'delete') continue

    const snapshot = parseFeedLogFingerprintSource(entry.payload)
    if (!snapshot) continue
    deleteFingerprints.add(buildFeedLogFingerprint(snapshot))
  }

  return { ids, deleteFingerprints }
}

const hasPendingFeedLogOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false
  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox.where('[entityType+entityId]').equals(['feedLog', trimmed]).first()
    return Boolean(match)
  } catch {
    return false
  }
}

const reconcileRemoteFeedLogsIntoDexie = async (cached: FeedLog[], remoteMapped: FeedLog[]): Promise<void> => {
  // Reconcile historical client-generated IDs vs server-generated UUIDs.
  // If we already have a local row with the same "event" semantics but a different id,
  // drop the local copy so the UI doesn't show duplicates.
  const cachedByFingerprint = new Map<string, string[]>()
  for (const log of cached) {
    const fp = buildFeedLogFingerprint(log)
    const list = cachedByFingerprint.get(fp)
    if (list) list.push(log.id)
    else cachedByFingerprint.set(fp, [log.id])
  }

  const deleteCandidates = new Set<string>()
  for (const remoteLog of remoteMapped) {
    const fp = buildFeedLogFingerprint(remoteLog)
    const ids = cachedByFingerprint.get(fp)
    if (!ids) continue
    for (const id of ids) {
      if (id !== remoteLog.id) deleteCandidates.add(id)
    }
  }

  for (const id of deleteCandidates) {
    // Avoid deleting rows that still have pending local mutations.
    // (Those should be uploaded first, then we'll reconcile on the next refresh.)
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingFeedLogOutboxEntry(id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieFeedLogRepo.delete(id)
  }
}

const pruneRemovedRemoteFeedLogs = async (cached: FeedLog[], remoteMapped: FeedLog[]): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((log) => log.id))

  for (const log of cached) {
    if (remoteIds.has(log.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingFeedLogOutboxEntry(log.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieFeedLogRepo.delete(log.id)
  }
}

const normalizeRemoteFeedLog = (candidate: any): FeedLog | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const productionUnitId = typeof candidate.productionUnitId === 'string' ? candidate.productionUnitId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  if (!id || !farmId || !productionUnitId || !recordedAtRaw) return null

  return {
    id,
    farmId,
    productionUnitId,
    lotId: typeof candidate.lotId === 'string' ? candidate.lotId.trim() || undefined : undefined,
    lot: typeof candidate.lot === 'string' ? candidate.lot.trim() || undefined : undefined,
    capturedByActorId: typeof candidate.capturedByActorId === 'string' ? candidate.capturedByActorId.trim() || undefined : undefined,
    captureSource: toOptionalTrimmed(candidate.captureSource),
    recordedAt: normalizeRecordedAt(recordedAtRaw),
    feedKg: toOptionalDecimalString(candidate.feedKg),
    feedType: toOptionalTrimmed(candidate.feedType),
    sfrPercent: toOptionalDecimalString(candidate.sfrPercent),
    biomassKg: toOptionalDecimalString(candidate.biomassKg),
    fcr: toOptionalDecimalString(candidate.fcr),
    notes: toOptionalTrimmed(candidate.notes),
    metadata: typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
    createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
  }
}

export type FeedLogCreatePayload = {
  farmId: string
  productionUnitId: string
  lotId?: string
  lot?: string
  capturedByActorId?: string
  recordedAt?: string
  feedKg?: string | number
  feedType?: string
  sfrPercent?: string | number
  biomassKg?: string | number
  fcr?: string | number
  notes?: string
  metadata?: Record<string, unknown>
}

export type FeedLogWriteOptions = {
  // Charter O-3 / Feed v0 PRD §5: set only by Crew Mode's capture screen.
  // Tells the server to derive cost from authoritative inventory pricing
  // instead of the client-side derivation below — see FeedLogService's
  // reconcileFeedLogCost. The admin Feed.tsx / CSV import paths never set
  // this and keep today's client-side-derivation behavior unchanged.
  deriveCostServerSide?: boolean
  // Server-Side Flag Enforcement v0 (docs/crew-mode/prd/crew-mode-server-side-
  // flag-enforcement-v0.md): set only by Crew Mode's capture screen. Never
  // persisted — carried only in the outbox payload for the server's
  // FeedLogService.create guard to read. Admin/CSV paths never set this.
  source?: 'crewMode'
}

export type FeedLogUpdatePayload = Partial<Omit<FeedLogCreatePayload, 'farmId' | 'productionUnitId'>>
export type FeedLogMutationResult = EntityMutationResult<FeedLog>
export type FeedLogDeleteResult = EntityMutationResult<void>

const normalizeFeedLogRecord = (args: {
  candidate: FeedLogCreatePayload | FeedLog
  id?: string
  createdAt?: string
  updatedAt?: string
}): FeedLog => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const productionUnitId = toOptionalTrimmed(args.candidate.productionUnitId)
  if (!productionUnitId) throw new Error('Production unit is required')

  const timestamp = nowIso()
  const candidateId = typeof (args.candidate as FeedLog).id === 'string'
    ? (args.candidate as FeedLog).id.trim()
    : ''

  return {
    id: args.id?.trim() || candidateId || createId('feed'),
    farmId,
    productionUnitId,
    lotId: toOptionalTrimmed(args.candidate.lotId),
    lot: toOptionalTrimmed(args.candidate.lot),
    capturedByActorId: toOptionalTrimmed(args.candidate.capturedByActorId),
    captureSource: toOptionalTrimmed((args.candidate as { captureSource?: unknown }).captureSource),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    feedKg: toOptionalDecimalString(args.candidate.feedKg),
    feedType: toOptionalTrimmed(args.candidate.feedType),
    sfrPercent: toOptionalDecimalString(args.candidate.sfrPercent),
    biomassKg: toOptionalDecimalString(args.candidate.biomassKg),
    fcr: toOptionalDecimalString(args.candidate.fcr),
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

/**
 * Resolves a FeedLog to a canonical lotId, first checking the FeedLog's own
 * lotId field, then falling back to a Dexie lookup by (farmId, lotKey).
 *
 * v2.2 T3-B-06a (2026-07-15): the primary UI write path in
 * [src/screens/Feed.tsx](../screens/Feed.tsx) now sends `lotId` alongside
 * `lot` (lotKey) on both create and update, so new FeedLogs land with
 * `lotId` populated in the DB. This helper's fallback path is still
 * required for legacy rows saved before 06a landed (they carry only
 * `lot`), for CSV imports that supply only a lot label, and for
 * defense-in-depth on the auto-derivation path. If the referenced lot
 * doesn't yet exist locally, we silently skip cost derivation so the
 * FeedLog write itself never fails.
 *
 * Returns undefined when no lot can be resolved.
 */
const resolveFeedLogLotId = async (log: FeedLog): Promise<string | undefined> => {
  if (log.lotId?.trim()) return log.lotId.trim()
  const lotKey = log.lot?.trim()
  if (!lotKey) return undefined
  const matches = await dexieLotRepo.list({ farmId: log.farmId, lotKey })
  // dexieLotRepo.list matches lotKey as a case-insensitive substring; require
  // an exact match here so we never accidentally pair a FeedLog with a
  // look-alike lot (e.g. "LOT-1" vs "LOT-10").
  const exact = matches.find((lot) => lot.lotKey === lotKey)
  return exact?.id
}

/**
 * Applies auto-derivation for a single FeedLog: upsert-by-source-event-id.
 * If cost can be derived and lotId resolved, find any existing paired cost
 * event (via Dexie mirror scan by sourceEventType='feedLog' + sourceEventId),
 * then update in place OR create anew. If cost cannot be derived but a paired
 * cost event exists, delete it. All writes go through lotCostEventApi so the
 * outbox handles server sync (delete-parity for I-2 per finding #65 pattern).
 */
const applyFeedLogCostDerivation = async (
  log: FeedLog,
  inventoryItems: readonly InventoryItem[]
): Promise<void> => {
  const derived = deriveFeedLogCost(log, inventoryItems)
  const paired = await dexieLotCostEventRepo.list({
    sourceEventType: 'feedLog',
    sourceEventId: log.id
  })
  const existingPaired = paired[0]

  if (!derived) {
    // Feed no longer costs — delete the paired event if any.
    if (existingPaired) {
      await lotCostEventApi.delete(existingPaired.id)
    }
    return
  }

  const lotId = await resolveFeedLogLotId(log)
  if (!lotId) {
    if (import.meta.env.DEV) {
      console.warn('[feedLogApi] cost derivation skipped: cannot resolve lotId for FeedLog', {
        feedLogId: log.id,
        lot: log.lot,
        farmId: log.farmId
      })
    }
    if (existingPaired) {
      // Formerly matched; now unresolvable → delete stale paired event.
      await lotCostEventApi.delete(existingPaired.id)
    }
    return
  }

  if (existingPaired) {
    await lotCostEventApi.update(existingPaired.id, {
      recordedAt: log.recordedAt,
      amountCents: derived.amountCents,
      currency: derived.currency
    })
    return
  }

  await lotCostEventApi.create({
    farmId: log.farmId,
    lotId,
    productionUnitId: log.productionUnitId,
    recordedAt: log.recordedAt,
    categoryPrefix: 'N',
    amountCents: derived.amountCents,
    currency: derived.currency,
    sourceEventType: 'feedLog',
    sourceEventId: log.id,
    metadata: { matchedInventoryItemId: derived.matchedItemId }
  })
}

/**
 * Deletes the paired LotCostEvent for a FeedLog (if any exists in the Dexie
 * mirror). Delete-parity per I-2 / finding #65.
 */
const deletePairedCostEventForFeedLog = async (feedLogId: string): Promise<void> => {
  const paired = await dexieLotCostEventRepo.list({
    sourceEventType: 'feedLog',
    sourceEventId: feedLogId
  })
  for (const event of paired) {
    // eslint-disable-next-line no-await-in-loop
    await lotCostEventApi.delete(event.id)
  }
}

export const feedLogApi = {
  list: async (
    params: FeedLogListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<FeedLog[]> => {
    await ensureOfflineReady()

    const [cached, pendingFeedMarkers] = await Promise.all([
      dexieFeedLogRepo.list(params),
      getPendingFeedLogSyncMarkers()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)
    if (options?.preferLocal || !shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.feedLogs.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lotId: params.lotId,
        lot: params.lot,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteFeedLog)
        .filter((log): log is FeedLog => Boolean(log))
        .filter((log) => !pendingFeedMarkers.ids.has(log.id))
        .filter((log) => !pendingFeedMarkers.deleteFingerprints.has(buildFeedLogFingerprint(log)))

      await reconcileRemoteFeedLogsIntoDexie(cached, mapped)
      await pruneRemovedRemoteFeedLogs(cached, mapped)

      await dexieFeedLogRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieFeedLogRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[feedLogApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<FeedLog | undefined> => {
    await ensureOfflineReady()
    return dexieFeedLogRepo.getById(id)
  },

  create: async (payload: FeedLogCreatePayload, options?: FeedLogWriteOptions): Promise<FeedLogMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const log = normalizeFeedLogRecord({
      candidate: payload,
      id: createId('feed'),
      createdAt: timestamp,
      updatedAt: timestamp
    })
    const deriveCostServerSide = options?.deriveCostServerSide === true

    await dexieFeedLogRepo.upsert(log)
    await enqueueEntityChange({
      entityType: 'feedLog',
      op: 'upsert',
      entityId: log.id,
      payload: {
        ...log,
        id: log.id,
        ...(deriveCostServerSide ? { deriveCostServerSide: true } : {}),
        ...(options?.source ? { source: options.source } : {})
      }
    })

    if (deriveCostServerSide) {
      // Charter O-3 / Feed v0 PRD §5: Crew Mode never computes cost
      // client-side — the flag above tells the server to derive it from
      // authoritative, synced inventory pricing once this entry syncs.
    } else {
      // Admin Feed.tsx / CSV import path — unchanged. Auto-derive a paired
      // LotCostEvent from inventory unit-cost lookup. Any failure here must
      // NOT break the primary FeedLog write (I-2 pairing is best-effort at
      // the boundary; the primary event is authoritative).
      try {
        const inventoryItems = await inventoryApi.listAll()
        await applyFeedLogCostDerivation(log, inventoryItems)
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[feedLogApi.create] cost derivation failed', error)
        }
      }
    }

    return buildQueuedEntityMutationResult(log, {
      entityType: 'feedLog',
      entityId: log.id
    })
  },

  saveImportedLogs: async (logs: FeedLog[]): Promise<FeedLog[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) => normalizeFeedLogRecord({
      candidate: log,
      createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
      updatedAt: timestamp
    }))

    await dexieFeedLogRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(normalizedLogs.map((log) => ({
      entityType: 'feedLog' as const,
      op: 'upsert' as const,
      entityId: log.id,
      payload: {
        ...log,
        id: log.id
      }
    })))

    try {
      const inventoryItems = await inventoryApi.listAll()
      for (const log of normalizedLogs) {
        // eslint-disable-next-line no-await-in-loop
        await applyFeedLogCostDerivation(log, inventoryItems)
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[feedLogApi.saveImportedLogs] cost derivation failed', error)
      }
    }

    return normalizedLogs
  },

  update: async (id: string, patch: FeedLogUpdatePayload, options?: FeedLogWriteOptions): Promise<FeedLogMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieFeedLogRepo.getById(id)
    if (!existing) throw new Error('Feed log not found')

    const next: FeedLog = {
      ...existing,
      // v2.2 T3-B-06a: propagate `lotId` from the patch. Prior to this fix
      // an operator edit dropped `lotId` silently (only `lot` was carried
      // forward), which is how legacy FeedLogs were losing their canonical
      // lot reference. Uses the same hasOwnProperty gate as the other
      // fields so an omitted patch key preserves the existing value.
      lotId: Object.prototype.hasOwnProperty.call(patch, 'lotId') ? toOptionalTrimmed(patch.lotId) : existing.lotId,
      lot: Object.prototype.hasOwnProperty.call(patch, 'lot') ? toOptionalTrimmed(patch.lot) : existing.lot,
      capturedByActorId: Object.prototype.hasOwnProperty.call(patch, 'capturedByActorId') ? toOptionalTrimmed(patch.capturedByActorId) : existing.capturedByActorId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      feedKg: Object.prototype.hasOwnProperty.call(patch, 'feedKg') ? toOptionalDecimalString(patch.feedKg) : existing.feedKg,
      feedType: Object.prototype.hasOwnProperty.call(patch, 'feedType') ? toOptionalTrimmed(patch.feedType) : existing.feedType,
      sfrPercent: Object.prototype.hasOwnProperty.call(patch, 'sfrPercent') ? toOptionalDecimalString(patch.sfrPercent) : existing.sfrPercent,
      biomassKg: Object.prototype.hasOwnProperty.call(patch, 'biomassKg') ? toOptionalDecimalString(patch.biomassKg) : existing.biomassKg,
      fcr: Object.prototype.hasOwnProperty.call(patch, 'fcr') ? toOptionalDecimalString(patch.fcr) : existing.fcr,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes') ? toOptionalTrimmed(patch.notes) : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata') ? patch.metadata : existing.metadata,
      updatedAt: nowIso()
    }
    const deriveCostServerSide = options?.deriveCostServerSide === true

    await dexieFeedLogRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'feedLog',
      op: 'upsert',
      entityId: next.id,
      payload: {
        ...next,
        id: next.id,
        ...(deriveCostServerSide ? { deriveCostServerSide: true } : {})
      }
    })

    if (!deriveCostServerSide) {
      try {
        const inventoryItems = await inventoryApi.listAll()
        await applyFeedLogCostDerivation(next, inventoryItems)
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[feedLogApi.update] cost derivation failed', error)
        }
      }
    }

    return buildQueuedEntityMutationResult(next, {
      entityType: 'feedLog',
      entityId: next.id
    })
  },

  // Findings #66: every delete carries a reason; the server audits it
  // (CrewDataCorrection with the full prior row) when the outbox syncs.
  delete: async (id: string, audit: { reason: string; operatorActorId?: string }): Promise<FeedLogDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieFeedLogRepo.getById(id)

    await dexieFeedLogRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'feedLog',
      op: 'delete',
      entityId: id,
      payload: existing ? { ...existing } : undefined,
      reason: audit.reason,
      operatorActorId: audit.operatorActorId
    })

    try {
      await deletePairedCostEventForFeedLog(id)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[feedLogApi.delete] paired cost event cleanup failed', error)
      }
    }

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'feedLog',
      entityId: id
    })
  },

  async readFeedLogSyncStatus(feedLogIds: string[]): Promise<EntityBatchSyncStatus> {
    return readEntityBatchSyncStatus('feedLog', feedLogIds)
  },

  async retryFeedLogSyncStatusWithReport(feedLogIds: string[]): Promise<EntityBatchSyncRetryReport> {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'feedLog',
      entityIds: feedLogIds,
      maxEntries: 100,
      maxPasses: 5
    })
  }
}
